import { type Ref, watch } from 'vue'
import type { LocationQuery, LocationQueryRaw } from 'vue-router'
import { useRoute, useRouter } from 'vue-router'
import { debugConsoleError } from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import {
  CHAT_LIST_TAB,
  type ChatListTab,
} from '@/modules/chat-matrix-fast/constants/chat'
import { useChatStore } from '@/modules/chat-matrix-fast/stores/chatViewStore'
import { useChatStore as useChatStoreCore } from '@/modules/chat-matrix-fast/stores/chatStore'
import { setSuppressAutoReadReceipt } from '@/modules/chat-matrix-fast/stores/core/runtime'
import { ChatApiService } from '@/modules/chat-matrix-fast/services/chatApiService'
import type { IChat } from '@/modules/chat-matrix-fast/types/IChat'
import { GROUP_INVITE_UI_ENABLED } from '@/modules/chat-matrix-fast/utils/groupInviteLink'

export const CHAT_URL_QUERY = {
  TAB: 'tab',
  CHAT: 'chat',
  INVITE: 'invite',
} as const

export const CHAT_URL_TAB = {
  CHATS: 'chats',
  EVENTS: 'events',
} as const

const PENDING_INVITE_KEY = 'matrixChatPendingInvite'

type PendingInvite = {
  chatId: number
  isInvite: boolean
  tab: ChatListTab | null
}

export function chatListTabToUrlTab(tab: ChatListTab): string {
  return tab === CHAT_LIST_TAB.EVENTS
    ? CHAT_URL_TAB.EVENTS
    : CHAT_URL_TAB.CHATS
}

export function urlTabToChatListTab(tab: unknown): ChatListTab | null {
  const value = Array.isArray(tab) ? tab[0] : tab

  if (value === CHAT_URL_TAB.EVENTS) {
    return CHAT_LIST_TAB.EVENTS
  }

  if (value === CHAT_URL_TAB.CHATS) {
    return CHAT_LIST_TAB.EMPLOYEES
  }

  return null
}

export function parseChatIdFromQuery(value: unknown): number | null {
  const raw = Array.isArray(value) ? value[0] : value
  const id = Number(raw)

  return Number.isFinite(id) && id > 0 ? id : null
}

function parseInviteFlag(value: unknown): boolean {
  const raw = Array.isArray(value) ? value[0] : value
  return raw === '1' || raw === 'true'
}

/** Читает chat/invite из location — устойчиво к &amp; в query. */
function readInviteFromLocation(): PendingInvite | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const url = new URL(window.location.href)
    const params = url.searchParams

    const chatRaw = params.get(CHAT_URL_QUERY.CHAT)
      || params.get('amp;chat')
      || params.get('amp%3Bchat')

    const chatId = parseChatIdFromQuery(chatRaw)
    if (chatId == null) {
      return null
    }

    const tab = urlTabToChatListTab(params.get(CHAT_URL_QUERY.TAB))
    const isInvite = parseInviteFlag(params.get(CHAT_URL_QUERY.INVITE))

    return { chatId, isInvite, tab }
  } catch {
    return null
  }
}

function stashPendingInvite(pending: PendingInvite): void {
  try {
    sessionStorage.setItem(PENDING_INVITE_KEY, JSON.stringify(pending))
  } catch {
    // ignore
  }
}

function takePendingInvite(): PendingInvite | null {
  try {
    const raw = sessionStorage.getItem(PENDING_INVITE_KEY)
    if (!raw) {
      return null
    }
    sessionStorage.removeItem(PENDING_INVITE_KEY)
    const parsed = JSON.parse(raw) as PendingInvite
    if (!parsed?.chatId || !Number.isFinite(Number(parsed.chatId))) {
      return null
    }

    return {
      chatId: Number(parsed.chatId),
      isInvite: Boolean(parsed.isInvite),
      tab: parsed.tab ?? null,
    }
  } catch {
    return null
  }
}

function peekPendingInvite(): PendingInvite | null {
  try {
    const raw = sessionStorage.getItem(PENDING_INVITE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as PendingInvite
    if (!parsed?.chatId || !Number.isFinite(Number(parsed.chatId))) {
      return null
    }

    return {
      chatId: Number(parsed.chatId),
      isInvite: Boolean(parsed.isInvite),
      tab: parsed.tab ?? null,
    }
  } catch {
    return null
  }
}

function clearPendingInvite(): void {
  try {
    sessionStorage.removeItem(PENDING_INVITE_KEY)
  } catch {
    // ignore
  }
}

/** Прямая ссылка на чат в /messenger (invite/share). */
export function buildChatShareUrl(chatId: number, tab: ChatListTab = CHAT_LIST_TAB.EVENTS): string {
  const url = new URL('/messenger', window.location.origin)

  url.searchParams.set(CHAT_URL_QUERY.TAB, chatListTabToUrlTab(tab))
  url.searchParams.set(CHAT_URL_QUERY.CHAT, String(chatId))

  return url.toString()
}

export function buildGroupInviteUrl(
  chatId: number,
  meta?: { title?: string | null; type?: string | null },
): string {
  const url = new URL(buildChatShareUrl(chatId, CHAT_LIST_TAB.EMPLOYEES))
  url.searchParams.set(CHAT_URL_QUERY.INVITE, '1')

  const title = meta?.title?.trim()
  if (title) {
    url.searchParams.set('title', title.slice(0, 120))
  }

  const type = meta?.type?.trim()
  if (type) {
    url.searchParams.set('gtype', type.slice(0, 32))
  }

  return url.toString()
}

async function copyChatShareLink(
  link: string,
  successMessage: string,
): Promise<void> {
  const { useFeedbackStore } = await import('@/modules/feedback/stores/feedbackStore')
  const feedbackStore = useFeedbackStore()

  try {
    await navigator.clipboard.writeText(link)
    feedbackStore.showToast({
      type: 'success',
      message: successMessage,
      time: 3000,
    })
  } catch (error) {
    debugConsoleError('Не удалось скопировать ссылку:', error)
    feedbackStore.showToast({
      type: 'error',
      message: 'Не удалось скопировать ссылку',
      time: 3000,
    })
  }
}

export async function copyEventChatShareLink(chatId: number): Promise<void> {
  await copyChatShareLink(
    buildChatShareUrl(chatId, CHAT_LIST_TAB.EVENTS),
    'Ссылка на чат скопирована',
  )
}

/** Прямая ссылка на группу/канал (открывает чат у участника). */
export async function copyGroupShareLink(chatId: number): Promise<void> {
  await copyChatShareLink(
    buildChatShareUrl(chatId, CHAT_LIST_TAB.EMPLOYEES),
    'Ссылка на группу скопирована',
  )
}

/** Ссылка-приглашение в группу (как в Telegram). */
export async function copyGroupInviteLink(
  chatId: number,
  meta?: { title?: string | null; type?: string | null },
): Promise<void> {
  if (!GROUP_INVITE_UI_ENABLED) {
    return
  }

  let resolved = meta
  if (!resolved?.title?.trim()) {
    try {
      const { useChatStore } = await import('@/modules/chat-matrix-fast/stores/chatStore')
      const core = useChatStore()
      const chat = core.currentChat?.id === chatId
        ? core.currentChat
        : core.chats.find(item => item.id === chatId)
      if (chat) {
        resolved = {
          title: chat.title,
          type: chat.type,
          ...meta,
        }
      }
    } catch {
      // ignore
    }
  }

  await copyChatShareLink(
    buildGroupInviteUrl(chatId, resolved),
    'Ссылка-приглашение скопирована',
  )
}

function isSameQuery(a: LocationQuery, b: LocationQueryRaw): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])

  for (const key of keys) {
    const left = a[key]
    const right = b[key]

    const leftStr = left == null ? '' : String(Array.isArray(left) ? left[0] : left)
    const rightStr = right == null ? '' : String(Array.isArray(right) ? right[0] : right)

    if (leftStr !== rightStr) {
      return false
    }
  }

  return true
}

function buildChatUrlQuery(
  currentQuery: LocationQuery,
  tab: ChatListTab,
  activeChatId: number | undefined,
): LocationQueryRaw {
  const preserved: LocationQueryRaw = {}
  for (const [key, value] of Object.entries(currentQuery)) {
    if (
      key === CHAT_URL_QUERY.TAB
      || key === CHAT_URL_QUERY.CHAT
      || key === CHAT_URL_QUERY.INVITE
      || key === 'amp;chat'
      || key === 'title'
      || key === 'gtype'
    ) {
      continue
    }
    if (value != null) {
      preserved[key] = value
    }
  }

  const query: LocationQueryRaw = {
    ...preserved,
    [CHAT_URL_QUERY.TAB]: chatListTabToUrlTab(tab),
  }

  if (activeChatId) {
    query[CHAT_URL_QUERY.CHAT] = String(activeChatId)
  }

  return query
}

async function resolveChatFromUrl(
  chatId: number,
  isInvite: boolean,
  chatStoreCore: ReturnType<typeof useChatStoreCore>,
): Promise<IChat> {
  if (!isInvite) {
    const cached = chatStoreCore.chats.find(chat => Number(chat.id) === Number(chatId))
    if (cached?.matrix_room_id?.trim()) {
      return cached
    }
  }

  try {
    return await chatStoreCore.fetchChat(chatId)
  } catch (error) {
    if (!GROUP_INVITE_UI_ENABLED) {
      throw error
    }
    // Не участник / нет доступа — вступаем по ссылке.
  }

  const joined = await ChatApiService.joinByInvite(chatId)
  chatStoreCore.upsertChatInList(joined)

  try {
    return await chatStoreCore.fetchChat(chatId)
  } catch {
    return joined
  }
}

function resolvePendingFromRoute(routeQuery: LocationQuery): PendingInvite | null {
  const fromLocation = readInviteFromLocation()
  if (fromLocation) {
    return fromLocation
  }

  const chatId = parseChatIdFromQuery(routeQuery[CHAT_URL_QUERY.CHAT])
    ?? parseChatIdFromQuery(routeQuery['amp;chat'])

  if (chatId == null) {
    return null
  }

  const tab = urlTabToChatListTab(routeQuery[CHAT_URL_QUERY.TAB])
  const isInvite = parseInviteFlag(routeQuery[CHAT_URL_QUERY.INVITE])

  return { chatId, isInvite, tab }
}

export function useChatUrlSync(isInitialized: Ref<boolean>) {
  const route = useRoute()
  const router = useRouter()
  const store = useChatStore()
  const chatStoreCore = useChatStoreCore()

  let isApplyingFromUrl = false

  // Сразу сохраняем invite — syncUrl может стереть query до restore.
  const bootPending = resolvePendingFromRoute(route.query)
  if (bootPending) {
    stashPendingInvite(bootPending)
  }

  const syncUrl = () => {
    if (isApplyingFromUrl || !isInitialized.value) {
      return
    }

    const pending = peekPendingInvite()
    if (pending) {
      const activeId = store.activeChat?.chat?.id
      const alreadyOpen = activeId != null && Number(activeId) === Number(pending.chatId)
      const tabMismatch = pending.tab != null && pending.tab !== store.chatListTab
      const chatMismatch = activeId != null && Number(activeId) !== Number(pending.chatId)

      // Deep-link уже открыт или пользователь ушёл на другой таб/чат — pending больше не нужен.
      if (alreadyOpen || tabMismatch || chatMismatch) {
        clearPendingInvite()
      }
      else {
        // Ещё идёт restore по ссылке — не затираем query.
        return
      }
    }

    const nextQuery = buildChatUrlQuery(
      route.query,
      store.chatListTab,
      store.activeChat?.chat?.id,
    )

    if (isSameQuery(route.query, nextQuery)) {
      return
    }

    void router.replace({
      path: route.path,
      query: nextQuery,
    })
  }

  const openChatFromUrl = async (
    chatId: number,
    isInvite: boolean,
    options?: { restoreSession?: boolean },
  ): Promise<boolean> => {
    if (options?.restoreSession) {
      setSuppressAutoReadReceipt(true)
    }

    try {
      const wasKnown = chatStoreCore.chats.some(chat => Number(chat.id) === Number(chatId))
      const fetchedChat = await resolveChatFromUrl(chatId, isInvite, chatStoreCore)

      await store.ensureChatListTabForChat(fetchedChat)
      await store.getChat(chatId)

      let opened = Number(store.activeChat?.chat?.id) === Number(chatId)
      if (!opened) {
        await store.getChat(chatId)
        opened = Number(store.activeChat?.chat?.id) === Number(chatId)
      }

      if (opened && (isInvite || !wasKnown)) {
        await store.fetchChatsAndContacts('')
      }

      if (opened) {
        clearPendingInvite()
      }

      return opened
    } catch (error) {
      debugConsoleError('Не удалось открыть чат по ссылке:', error)
      const { useFeedbackStore } = await import('@/modules/feedback/stores/feedbackStore')
      useFeedbackStore().showToast({
        type: 'error',
        message: 'Не удалось открыть группу по ссылке',
        time: 4000,
      })

      return false
    } finally {
      if (options?.restoreSession) {
        setSuppressAutoReadReceipt(false)
      }
    }
  }

  const restoreFromUrl = async (): Promise<boolean> => {
    const pending = takePendingInvite()
      || resolvePendingFromRoute(route.query)

    const urlTab = pending?.tab ?? urlTabToChatListTab(route.query[CHAT_URL_QUERY.TAB])
    const chatId = pending?.chatId ?? null
    const isInvite = pending?.isInvite ?? false

    if (!urlTab && chatId == null) {
      return false
    }

    isApplyingFromUrl = true

    try {
      if (chatId != null) {
        // Пока открываем — pending остаётся, syncUrl не сотрёт query.
        stashPendingInvite({ chatId, isInvite, tab: urlTab })
        const opened = await openChatFromUrl(chatId, isInvite, { restoreSession: true })
        if (!opened) {
          // Оставляем pending для повтора / не чистим URL.
          stashPendingInvite({ chatId, isInvite, tab: urlTab })
        }

        return opened
      }

      if (urlTab && urlTab !== store.chatListTab) {
        await store.setChatListTab(urlTab)
      }

      return Boolean(urlTab)
    } finally {
      isApplyingFromUrl = false
      syncUrl()
    }
  }

  watch(
    () => [
      store.chatListTab,
      store.activeChat?.chat?.id,
    ],
    syncUrl,
  )

  watch(
    () => route.query,
    async () => {
      if (!isInitialized.value || isApplyingFromUrl) {
        return
      }

      const incoming = resolvePendingFromRoute(route.query)
      const urlTab = incoming?.tab ?? urlTabToChatListTab(route.query[CHAT_URL_QUERY.TAB])
      const chatId = incoming?.chatId ?? null
      const isInvite = incoming?.isInvite ?? false

      const activeId = store.activeChat?.chat?.id
      const needsTabSwitch = Boolean(urlTab && urlTab !== store.chatListTab)
      const needsChatOpen = chatId != null && Number(activeId) !== Number(chatId)
      const isBareMessenger = !urlTab && chatId == null

      // Голый /messenger (меню, ссылка без query) — обычные чаты, не задачи.
      if (isBareMessenger) {
        clearPendingInvite()
        if (store.chatListTab !== CHAT_LIST_TAB.EMPLOYEES) {
          isApplyingFromUrl = true
          try {
            await store.setChatListTab(CHAT_LIST_TAB.EMPLOYEES)
          } finally {
            isApplyingFromUrl = false
            syncUrl()
          }
        }
        else {
          syncUrl()
        }

        return
      }

      // Нет целевого deep-link — только синхронизируем URL из состояния UI.
      if (!needsTabSwitch && !needsChatOpen) {
        if (chatId != null && Number(activeId) === Number(chatId)) {
          clearPendingInvite()
        }
        syncUrl()

        return
      }

      isApplyingFromUrl = true

      try {
        if (needsTabSwitch && urlTab) {
          await store.setChatListTab(urlTab)
        }

        if (needsChatOpen && chatId != null) {
          stashPendingInvite({ chatId, isInvite, tab: urlTab })
          await openChatFromUrl(chatId, isInvite)
        }
      } finally {
        isApplyingFromUrl = false
        syncUrl()
      }
    },
  )

  return {
    restoreFromUrl,
    syncUrl,
    buildChatShareUrl,
    buildGroupInviteUrl,
  }
}
